import { useUserSession } from '@solinkcorp/authentication';
import { useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import { VmsApi } from 'clients';
import { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import useSWR from 'swr';
import { getLocalizedStr } from 'utils/LocaleUtil';

const SUPPORTED_VERSION = 130; // System requirement check is supported in i130 major versions or higher

const TOOLTIP_MATCHERS = ['VMS', 'monitor', 'model'];
const loggedDevices = new Set<string>(); // de-duplicate log warnings for the same device
type TooltipKey = (typeof TOOLTIP_MATCHERS)[number];

const useVisionAnalyticsSystemRequirement = ({
  deviceUrl,
  currentSoftwareMajorVersion,
  isVisionAnalyticsEnabled,
  deviceName,
}: {
  deviceUrl: string | null;
  currentSoftwareMajorVersion?: number;
  isVisionAnalyticsEnabled?: boolean;
  deviceName?: string;
}) => {
  const locale = useLocale();
  const user = useUserSession();
  const isVersionSupported = useMemo(
    () => currentSoftwareMajorVersion && currentSoftwareMajorVersion >= SUPPORTED_VERSION,
    [currentSoftwareMajorVersion],
  );

  const key = useMemo(
    () =>
      deviceUrl && isVersionSupported && isVisionAnalyticsEnabled
        ? `allowedCameras-${deviceUrl}`
        : null,
    [deviceUrl, isVersionSupported, isVisionAnalyticsEnabled],
  );

  const staleConfig = useStaleSWRConfig(key, false);

  const { data } = useSWR(
    key,
    () => fetchSystemRequirements(deviceUrl!, deviceName ?? '', user.accessToken),
    {
      errorRetryCount: 0,
      ...staleConfig,
    },
  );

  const tooltipMessages: Record<string, string> = useMemo(
    () => ({
      VMS: locale.peopleTracking.requirementConnectedCameras,
      monitor: locale.peopleTracking.requirementMonitor,
      model: locale.peopleTracking.requirementModel,
    }),
    [
      locale.peopleTracking.requirementConnectedCameras,
      locale.peopleTracking.requirementMonitor,
      locale.peopleTracking.requirementModel,
    ],
  );

  const rawToolTipMsg = getToolTipMessage(data?.reasons, tooltipMessages);
  const toolTipMsg =
    rawToolTipMsg && rawToolTipMsg.includes('__maxCameras__')
      ? getLocalizedStr({
          string: rawToolTipMsg,
          replacements: { maxCameras: String(data?.max_cameras ?? '—') },
        })
      : (rawToolTipMsg ?? '');

  return useMemo(
    () => ({
      maxCameraTrackers: data?.max_cameras,
      toolTipMsg,
    }),
    [data?.max_cameras, toolTipMsg],
  );
};

export default useVisionAnalyticsSystemRequirement;

async function fetchSystemRequirements(deviceUrl: string, deviceName: string, token: string) {
  try {
    const res = await VmsApi(deviceUrl, false, token).getVisionAnalyticsSystemRequirement();

    if (res?.reasons && res?.reasons.length > 0) {
      loggedDevices.add(deviceUrl);
      console.log(
        `%c[WARNING] - Vision Analytics System Requirement\n\n api endpoint: ${deviceUrl}\n- ${res.reasons.join('\n- ')}`,
        'color: orange',
      );
    }

    return res;
  } catch (error: any) {
    console.log(error);
  }
}

function getToolTipMessage(
  reasons: string[] | undefined,
  messages: Record<TooltipKey, string>,
): string {
  if (!reasons?.length) return '';

  const matchedKey = TOOLTIP_MATCHERS.find((match) =>
    reasons?.some((reason) => reason.includes(match)),
  );

  return matchedKey
    ? messages[`${matchedKey}`]
    : (reasons?.join('\n- ') ?? 'condition not accounted for');
}
